//@ts-check

// Imports
import React, { useState, useEffect, useRef, Suspense } from "react"
import _ from "lodash"
import { useTranslation } from "react-i18next"

// Internal
import InputComponent from "../components/input-component/input-component"
import OutputComponent from "../components/output-component"
import CompilationErrorsBox from "../components/compilation-errors"
import useStyles from "../components/styles"
import Button from "../components/button"
import useFeature from "../utils/subscriptionWrapper"
import CONSTS from "shared/consts"
import AutomaticTestingToolHeader from "./../components/automatic-testing-tool-header";
import isSelectedContractExists from "../components/input-component/is-selected-contact-exists"
import useEditorHeight from "../components/input-component/use-editor-height"
import { trackAndCall } from "src/utils/tracking"
import Disclaimer from "../components/disclaimer"

/**
 * @param props{{
 * files
 * isLoading
 * response
 * setFiles
 * submitCode
 * compilerVersion
 * setCompilerVersion
 * clickNewCustomTest?
 * clickCustomTest?
 * customTests?
 * deleteCustomTest?
 * selectedContract
 * setSelectedContract
 * mainContract
 * setMainFile
 * mainFile
 * setMainContract
 * importPopoverAnchorEl
 * setImportPopoverAnchorEl
 * setABI?
 * addLeavePageHandlers?
 * removeLeavePageHandlers?
 * title?
 * description?
 * compilerSettings
 * setCompilerSettings
 * }}
 */
export default function NonInteractive({
  files,
  isLoading,
  response,
  setFiles,
  submitCode,
  compilerVersion,
  setCompilerVersion,
  clickNewCustomTest,
  clickCustomTest,
  customTests,
  deleteCustomTest,
  mainContract,
  setMainFile,
  mainFile,
  setMainContract,
  importPopoverAnchorEl,
  setImportPopoverAnchorEl,
  setABI,
  addLeavePageHandlers,
  removeLeavePageHandlers,
  title,
  description,
  compilerSettings,
  setCompilerSettings,
}) {

  const { t } = useTranslation()
  // States:
  const [editorObject, setEditorObject] = useState(null)
  const [selectedFilePath, setSelectedFilePath] = useState(files?.[0]?.path)
  const [sourceLineNumber, setSourceLineNumber] = useState(null)


  // Effects
  const editorHeight = useEditorHeight()

  const isShowAllTests = useFeature(CONSTS.SUBSCRIPTIONS.ALL_SCR_TESTS)
  const isShowCategories = useFeature(CONSTS.SUBSCRIPTIONS.SHOW_SCR_CATEGORIES)

  // Constants:
  const editorLineHeight = 18

  // Variables:
  const sectionRef = useRef(null)
  const pageIntro = useRef(null)

  // Functions:

  const selectSourceCodeLine = (lineNumber = 0) => {
    let sourceScrollTop = 1
    if (lineNumber) {
      const scrollInPixels = lineNumber * editorLineHeight // scroll down to line to focus on
      const halfEditorHeight = editorHeight / 2 // scroll half way through editor, so that line in focus is half way down
      sourceScrollTop = scrollInPixels - halfEditorHeight // scroll down so that line in focus is half way down editor
    }
    if (editorObject) {
      editorObject.setScrollTop(sourceScrollTop)
      editorObject.setPosition({ column: 0, lineNumber })
    }
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }

  useEffect(() => {
    if (typeof sourceLineNumber === "number") {
      // first focus on specified line in monaco editor
      selectSourceCodeLine(sourceLineNumber)
      // then reset the line number, ready for the scrolling functionality to be called again
      setSourceLineNumber(null)
    }
  }, [sourceLineNumber])

  const scrollToSource = (lineNumber = 0, filePath) => {
    if (typeof filePath === "string") {
      setSelectedFilePath(filePath)
    }
    setSourceLineNumber(lineNumber)
  }

  const classes = useStyles({})

  return (
    <>
    <div ref={sectionRef} 
    css = {
      `
      padding: 35px;
      background: #F6F6FA;
      min-height: 100vh;
      `
    }
  >
      <Suspense fallback={null}>
      <div onMouseEnter={removeLeavePageHandlers}>
        <AutomaticTestingToolHeader title={title} description={description}></AutomaticTestingToolHeader>
        <Disclaimer />
        <hr css={`
          margin-top:15px;
          border: 1px solid #EAEAF2;
          `} 
        />
          <div
          css={`
          margin-top:25px;
          margin-bottom:100px;
        `}
          >
            <InputComponent
              skipSampleProjects
              onX={submitCode}
              ctaText="Scan"
              showValidationBtn = {false}
              files={files}
              setFiles={setFiles}
              isLoading={isLoading}
              compilerVersion={compilerVersion}
              setCompilerVersion={setCompilerVersion}
              mainContract={mainContract}
              compilerSettings={compilerSettings}
              setCompilerSettings={setCompilerSettings}
              setMainFile={setMainFile}
              mainFile={mainFile}
              setMainContract={setMainContract}
              selectedFilePath={selectedFilePath}
              setSelectedFilePath={setSelectedFilePath}
              importPopoverAnchorEl={importPopoverAnchorEl}
              setImportPopoverAnchorEl={setImportPopoverAnchorEl}
              setABI={setABI}
            />
          </div>
        </div>
        <div
          css={`
          margin-top:25px;
          margin-bottom:100px;
        `}
          >
          {!_.isEmpty(response.errors) && (
            <CompilationErrorsBox compilationErrors={response.errors} scrollToSource={scrollToSource} />
          )}
          {(!_.isEmpty(response.test_results)) && (
            <div onMouseEnter={addLeavePageHandlers}>
              <OutputComponent
                deleteCustomTest={deleteCustomTest}
                customTests={customTests}
                analysisOutput={response.test_results}
                onClickNewCustomText={clickNewCustomTest}
                clickCustomTest={clickCustomTest}
                scrollToSource={scrollToSource}
                isShowAllTests={isShowAllTests}
                isShowCategories={isShowCategories}
              />
            </div>
          )}
        </div>
      </Suspense>
    </div>
    </>
  )
}
